// d:\Code\debate-app\src\LandingPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set, get } from 'firebase/database';
import { db } from './firebase';
import {
  Container,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  // --- REMOVED: Divider ---
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid // --- ADDED: Grid for layout ---
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LoginIcon from '@mui/icons-material/Login';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';
import { Helmet } from 'react-helmet-async';
import AdSenseUnit from './components/AdSenseUnit';

export default function LandingPage() {
  const [type, setType] = useState('LD');
  const [joinId, setJoinId] = useState('');
  const [error, setError] = useState('');
  const [loadingStart, setLoadingStart] = useState(false);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const navigate = useNavigate();

  // Function to handle starting a new session (NO CHANGES NEEDED HERE)
  const handleStart = async () => {
    setError('');
    setLoadingStart(true);
    console.log("handleStart: Attempting to create new session...");

    const sessionId = Math.random().toString(36).substr(2, 8);
    const sessionRef = ref(db, `sessions/${sessionId}`);
    console.log(`handleStart: Generated Session ID: ${sessionId}`);

    const initialTotalPrepTime = 4 * 60 * 1000; // 4 minutes in ms

    try {
      console.log("handleStart: Attempting Firebase set operation...");
      await set(sessionRef, {
        type,
        roles: {}, // { roleName: userId }
        currentSegment: 0,
        segmentEndTime: null,
        totalPrepTime: initialTotalPrepTime,
        prepTimers: {
            sideA: { remaining: initialTotalPrepTime, running: false },
            sideB: { remaining: initialTotalPrepTime, running: false }
        },
        notes: {}, // { segmentIndex: noteText }
        participants: {} // { userId: { role: roleName, online: boolean, lastSeen: timestamp } } - Presence updated
      });
      console.log("handleStart: Firebase set successful.");
      console.log(`handleStart: Navigating to /session/${sessionId}`);
      navigate(`/session/${sessionId}`);

    } catch (err) {
      console.error("handleStart: Error creating session:", err);
      setError(`Failed to create session: ${err.message}. Check Firebase rules/config and console.`);
      setLoadingStart(false);
    }
  };

  // Function to handle joining an existing session (NO CHANGES NEEDED HERE)
  const handleJoin = async () => {
    setError('');
    setLoadingJoin(true);
    console.log(`handleJoin: Attempting to join session ${joinId}...`);

    if (!joinId.trim()) {
        setError('Please enter a Session ID.');
        setLoadingJoin(false);
        return;
    }

    const sessionRef = ref(db, `sessions/${joinId}`);

    try {
        console.log("handleJoin: Attempting Firebase get operation...");
        const snapshot = await get(sessionRef);
        console.log(`handleJoin: Firebase get result exists: ${snapshot.exists()}`);

        if (snapshot.exists()) {
          console.log(`handleJoin: Session found. Navigating to /session/${joinId}`);
          navigate(`/session/${joinId}`);
        } else {
          console.log("handleJoin: Session not found.");
          setError('Session ID not found.');
          setLoadingJoin(false);
        }
    } catch (err) {
        console.error("handleJoin: Error joining session:", err);
        setError(`Failed to check session: ${err.message}. Check Firebase connection and console.`);
        setLoadingJoin(false);
    }
  };

  const isLoading = loadingStart || loadingJoin;

  return (
    
    // Use "md" for wider content on desktop, still responsive
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: { xs: 2, sm: 3 } }}>
        Debate Session Manager
      </Typography>

      {/* --- Optional: Short Intro Text (Removed the Paper for brevity) --- */}
      <Typography variant="body1" component="p" align="center" sx={{ mb: { xs: 3, sm: 4 }, maxWidth: '95ch', mx: 'auto', color: 'text.secondary' }}>
        Manage live online debate rounds for **LD** and **PF**. Synchronized timers, roles, status, and judge notes.
      </Typography>

      {/* --- Grid Layout for Create/Join --- */}
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center" alignItems="stretch">

        {/* --- Grid Item 1: Create New Session --- */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" gutterBottom align="center">Create New Session</Typography>
              {/* Moved Type Selection Inside */}
              <FormControl component="fieldset" disabled={isLoading} fullWidth sx={{ mb: 2 }}>
                <FormLabel component="legend" align="center" sx={{ mb: 1, fontSize: '1rem' }}>Choose Format:</FormLabel>
                <RadioGroup
                  row aria-label="debate-type" name="debate-type-group"
                  value={type} onChange={(e) => setType(e.target.value)}
                  sx={{ justifyContent: 'center' }}
                >
                  <FormControlLabel value="LD" control={<Radio size="small"/>} label="LD" />
                  <FormControlLabel value="PF" control={<Radio size="small"/>} label="PF" />
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                Start a fresh round and get a unique Session ID to share.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}> {/* Ensure button is at the bottom */}
              <Button
                variant="contained" color="primary" onClick={handleStart}
                disabled={isLoading} size="large"
                startIcon={loadingStart ? <CircularProgress size={24} color="inherit" /> : <AddCircleOutlineIcon />}
              >
                {loadingStart ? 'Starting...' : 'Start New Round'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* --- Grid Item 2: Join Existing Session --- */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" gutterBottom align="center">Join Existing Session</Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                Enter the Session ID provided by the session creator.
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }} alignItems="center">
                <TextField
                  label="Session ID" variant="outlined" size="small"
                  value={joinId} onChange={e => setJoinId(e.target.value)}
                  disabled={isLoading} fullWidth
                  sx={{ maxWidth: 300 }} // Limit width for better centering
                />
              </Stack>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}> {/* Ensure button is at the bottom */}
              <Button
                variant="outlined" color="secondary" onClick={handleJoin}
                disabled={isLoading} size="large" // Make button size consistent
                startIcon={loadingJoin ? <CircularProgress size={24} color="inherit" /> : <LoginIcon />}
                fullWidth
                sx={{ maxWidth: 300 }} // Limit width
              >
                {loadingJoin ? 'Joining...' : 'Join Round'}
              </Button>
            </Box>
          </Paper>
        </Grid>

      </Grid> {/* --- End Grid Layout --- */}

      {/* Error Display Area - Placed below the grid */}
      {error && (
        <Alert severity="error" sx={{ mt: 3, maxWidth: 'sm', mx: 'auto' }}>
          {error}
        </Alert>
      )}

      {/* --- How it Works Section (Kept below, potentially less prominent) --- */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 4, sm: 5 }, mb: 2, bgcolor: 'background.default' }}>
        <Typography variant="h6" component="h2" gutterBottom align="center" sx={{ fontSize: '1.1rem' }}>
          How It Works
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}><LooksOneIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Select Format & Start/Join" secondary="Choose LD/PF, then start new or enter an ID to join." />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}><LooksTwoIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Choose Your Role" secondary="Select your position (Aff, Neg, Pro, Con, Judge) to enter." />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}><Looks3Icon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Manage the Round" secondary="Use shared timers. See who's online. Judges can take notes." />
          </ListItem>
           <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}><Looks4Icon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Real-Time Updates" secondary="Timers, status, and notes sync instantly via Firebase." />
          </ListItem>
        </List>
        <Typography variant="caption" component="p" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary', textAlign: 'center' }}>
            Note: Sessions are temporary and data is deleted shortly after the last participant leaves.
        </Typography>
      </Paper>
      {/* --- END: How it Works Section --- */}

    </Container>
    
  );
}
